import { useHandleResponse } from '@/utils/useHandleResponse';
import { CalendarOutlined, EditOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { DatesSetArg, EventClickArg, EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import FullCalendar from '@fullcalendar/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { TableProps } from 'antd';
import { Button, Col, DatePicker, Row, Segmented, Select, Spin, Table, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../context/AuthProvider';
import { updatePropsMyActiveMentenancePlannigs } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import { setMyActiveMentenancePlannings } from '../../../../../contextRedux/MentenancePlanning/MentenancePlanningStore';
import MentenancePlanningService from '../../../../../services/mentenancePlanning/MentenancePlanningService';
import { RootState } from '../../../../../store';
import '../../MentenancePlanningCalendarTab/MentenancePlanningCalendarTab.css';
import MentenancePlanningModal from '../../MentenancePlanningModal/MentenancePlanningModal';
import type {
  MaintenanceDateSortDirection,
  MaintenancePeriodMode,
} from '../maintenancePlanningViewUtils';
import {
  ACTIVE_STATUS_OPTIONS,
  MAINTENANCE_SORT_OPTIONS,
  PERIODICITY_OPTIONS,
  VIEW_TO_FULLCALENDAR,
  buildSystemTypeOptions,
  buildStatusOptionsWithCounts,
  buildUniqueFilterOptions,
  formatDisplayDate,
  getClientId,
  getClientName,
  getContractNumber,
  getDatePickerFormat,
  getDatePickerValue,
  getMaintenanceDisplayDate,
  getPeriodRangeForApi,
  getStatusDisplay,
  getWorkPlaceId,
  getWorkPlaceName,
  sortByMaintenanceDate,
} from '../maintenancePlanningViewUtils';

const MyActiveMentenancePlanning: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const handleResponse = useHandleResponse();
  const { profile } = useAuth();

  const reduxMentenancePlanning = useSelector((state: RootState) => state?.models?.mentenancePlanning);
  const mentenancePlannings = useSelector(
    (state: RootState) => state?.store?.mentenancePlanning?.myActiveRequestMentenancePlanningData ?? [],
  );

  const searchTerm = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.searchTerm,
  );
  const skip = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.skip,
  );
  const take = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.take,
  );
  const refresh = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsMyActiveMentenancePlannigs?.refresh,
  );

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const currentPage =
    Math.floor(
      reduxMentenancePlanning?.propsMyActiveMentenancePlannigs?.skip /
        reduxMentenancePlanning?.propsMyActiveMentenancePlannigs?.take,
    ) + 1;

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const [periodMode, setPeriodMode] = useState<MaintenancePeriodMode>('month');
  const [periodFocusDate, setPeriodFocusDate] = useState<Dayjs>(dayjs());
  const [selectedStatusFilterIds, setSelectedStatusFilterIds] = useState<number[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [selectedWorkPlaceIds, setSelectedWorkPlaceIds] = useState<string[]>([]);
  const [selectedPeriodicityFilters, setSelectedPeriodicityFilters] = useState<string[]>([]);
  const [selectedSystemTypeIds, setSelectedSystemTypeIds] = useState<string[]>([]);
  const [sortDirection, setSortDirection] = useState<MaintenanceDateSortDirection>('desc');
  const pageSizes = [10, 20, 30, 50];

  const selectedPeriodRange = useMemo(
    () => getPeriodRangeForApi(periodMode, periodFocusDate),
    [periodMode, periodFocusDate],
  );

  const searchFilters = useMemo(
    () => ({
      statusIds: selectedStatusFilterIds.length ? selectedStatusFilterIds : undefined,
      companyIds: selectedCompanyIds.length ? selectedCompanyIds : undefined,
      workPlaceIds: selectedWorkPlaceIds.length ? selectedWorkPlaceIds : undefined,
      periodicities: selectedPeriodicityFilters.length ? selectedPeriodicityFilters : undefined,
      systemTypeIds: selectedSystemTypeIds.length ? selectedSystemTypeIds : undefined,
      startDate: selectedPeriodRange.startDate,
      endDate: selectedPeriodRange.endDate,
      sortDirection,
    }),
    [
      selectedPeriodRange.endDate,
      selectedPeriodRange.startDate,
      selectedCompanyIds,
      selectedPeriodicityFilters,
      selectedStatusFilterIds,
      selectedSystemTypeIds,
      selectedWorkPlaceIds,
      sortDirection,
    ],
  );

  const statusCountFilters = useMemo(
    () => ({
      companyIds: selectedCompanyIds.length ? selectedCompanyIds : undefined,
      workPlaceIds: selectedWorkPlaceIds.length ? selectedWorkPlaceIds : undefined,
      periodicities: selectedPeriodicityFilters.length ? selectedPeriodicityFilters : undefined,
      systemTypeIds: selectedSystemTypeIds.length ? selectedSystemTypeIds : undefined,
      startDate: selectedPeriodRange.startDate,
      endDate: selectedPeriodRange.endDate,
    }),
    [
      selectedPeriodRange.endDate,
      selectedPeriodRange.startDate,
      selectedCompanyIds,
      selectedPeriodicityFilters,
      selectedSystemTypeIds,
      selectedWorkPlaceIds,
    ],
  );

  const { data: statusCounts = {} } = useQuery({
    queryKey: ['myActiveMentenanceStatusCounts', profile?.id, statusCountFilters],
    queryFn: async () => {
      const entries = await Promise.all(
        ACTIVE_STATUS_OPTIONS.map((option) =>
          MentenancePlanningService.searchMyActiveMentenancePlanning({
            assignedTechnicianId: profile?.id,
            isMyActiveMentenancePlannings: true,
            statusIds: [option.value],
            skip: 0,
            take: 1,
            ...statusCountFilters,
          }).then((data) => [option.value, data?.totalItems || 0] as const),
        ),
      );

      return Object.fromEntries(entries);
    },
    enabled: !!profile?.id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
  });

  const statusFilterOptions = useMemo(
    () => buildStatusOptionsWithCounts(ACTIVE_STATUS_OPTIONS, statusCounts),
    [statusCounts],
  );

  const filterOptionFilters = useMemo(
    () => ({
      statusIds: selectedStatusFilterIds.length ? selectedStatusFilterIds : undefined,
      startDate: selectedPeriodRange.startDate,
      endDate: selectedPeriodRange.endDate,
    }),
    [selectedPeriodRange.endDate, selectedPeriodRange.startDate, selectedStatusFilterIds],
  );

  const { data: filterOptionRecords = [], isFetching: isFilterOptionsLoading } = useQuery({
    queryKey: ['myActiveMentenanceFilterOptions', profile?.id, filterOptionFilters],
    queryFn: () =>
      MentenancePlanningService.searchMyActiveMentenancePlanning({
        assignedTechnicianId: profile?.id,
        isMyActiveMentenancePlannings: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isInterventionFileIncluded: true,
        skip: 0,
        take: 5000,
        ...filterOptionFilters,
      }).then((data) => data?.response ?? []),
    enabled: !!profile?.id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  });

  const clientFilterOptions = useMemo(
    () => buildUniqueFilterOptions(filterOptionRecords as any[], getClientId, getClientName),
    [filterOptionRecords],
  );

  const workPlaceFilterOptions = useMemo(
    () => buildUniqueFilterOptions(filterOptionRecords as any[], getWorkPlaceId, getWorkPlaceName),
    [filterOptionRecords],
  );

  const periodicityFilterOptions = useMemo(
    () => PERIODICITY_OPTIONS.map((periodicity) => ({ label: periodicity, value: periodicity })),
    [],
  );

  const systemTypeFilterOptions = useMemo(
    () => buildSystemTypeOptions(filterOptionRecords as any[]),
    [filterOptionRecords],
  );

  const resetToFirstPage = useCallback(() => {
    dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: 0 }));
  }, [dispatch]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'searchTerm', value: '' }));
    dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: calculatedSkip }));
    dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const { refetch, isFetching: isMentenancePlanningLoading } = useQuery({
    queryKey: ['myActiveMentenancePlannings', profile?.id, skip, take, refresh, searchFilters],
    queryFn: () =>
      MentenancePlanningService.searchMyActiveMentenancePlanning({
        assignedTechnicianId: profile?.id,
        isMyActiveMentenancePlannings: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isAssignedTechnicianIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isInterventionFileIncluded: true,
        skip,
        take,
        ...searchFilters,
      }).then((data) => {
        dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setMyActiveMentenancePlannings(data?.response));
        dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'refresh', value: false }));
        return data?.response;
      }),
    enabled: !!profile?.id,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: calendarData = [] } = useQuery({
    queryKey: [
      'myActiveMentenancePlanningsCalendar',
      profile?.id,
      viewMode,
      periodMode,
      periodFocusDate.format('YYYY-MM-DD'),
      searchFilters,
    ],
    queryFn: () =>
      viewMode === 'calendar'
        ? MentenancePlanningService.searchMyActiveMentenancePlanning({
            assignedTechnicianId: profile?.id,
            isMyActiveMentenancePlannings: true,
            isStatusIncluded: true,
            isCompanyIncluded: true,
            isContractIncluded: true,
            isWorkPlaceIncluded: true,
            isAssignedTechnicianIncluded: true,
            isMentenancePlanningSystemTypeIncluded: true,
            isInterventionFileIncluded: true,
            take: 5000,
            skip: 0,
            ...searchFilters,
          }).then((res) => res?.response ?? [])
        : Promise.resolve([]),
    enabled: !!profile?.id && viewMode === 'calendar',
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  });

  const calendarEvents = useMemo(() => {
    if (viewMode !== 'calendar') return [];
    return sortByMaintenanceDate(calendarData as any[], sortDirection)
      .map((record: any) => {
        const eventDate = getMaintenanceDisplayDate(record);
        if (!eventDate || !dayjs(eventDate).isValid()) return null;
        const statusDisplay = getStatusDisplay(record.statusId, record?.status?.name);
        return {
          id: record.id,
          title: `#${record.number || '-'} ${getClientName(record)}`,
          start: dayjs(eventDate).format('YYYY-MM-DD'),
          allDay: true,
          backgroundColor: statusDisplay.color,
          borderColor: statusDisplay.color,
          textColor: '#ffffff',
          extendedProps: { record, statusLabel: statusDisplay.label },
        };
      })
      .filter(Boolean);
  }, [calendarData, sortDirection, viewMode]);

  const { mutate: saveMentenancePlanning, isPending: isSavingMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, `Mentenanta ${data?.response?.number} a fost modificata cu succes`);
        if (data?.response?.statusId == 5) {
          MentenancePlanningService.sendNotification(data?.response);
        }
        refetch();
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const handleClientFilterChange = (value: string[]) => {
    setSelectedCompanyIds(value);
    resetToFirstPage();
  };

  const handleWorkPlaceFilterChange = (value: string[]) => {
    setSelectedWorkPlaceIds(value);
    resetToFirstPage();
  };

  const handleStatusFilterChange = (value: number[]) => {
    setSelectedStatusFilterIds(value);
    resetToFirstPage();
  };

  const handlePeriodicityFilterChange = (value: string[]) => {
    setSelectedPeriodicityFilters(value);
    resetToFirstPage();
  };

  const handleSystemTypeFilterChange = (value: string[]) => {
    setSelectedSystemTypeIds(value);
    resetToFirstPage();
  };

  const handlePeriodModeChange = (value: MaintenancePeriodMode) => {
    setPeriodMode(value);
    resetToFirstPage();
  };

  const handlePeriodDateChange = (value: Dayjs | null) => {
    setPeriodFocusDate(value ?? dayjs());
    resetToFirstPage();
  };

  const handleSortChange = (value: MaintenanceDateSortDirection) => {
    setSortDirection(value);
    resetToFirstPage();
  };

  const handleViewInfo = (record: any) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const handleCalendarEventClick = (arg: EventClickArg) => {
    const record = arg.event.extendedProps?.record;
    if (record) handleViewInfo(record);
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    setPeriodFocusDate(dayjs(arg.view.currentStart));
  };

  const renderEventContent = (eventContent: EventContentArg) => {
    const record = eventContent.event.extendedProps?.record as any;
    if (!record) return null;
    const statusDisplay = getStatusDisplay(record.statusId, record?.status?.name);
    return (
      <div className='maintenance-calendar-event-content'>
        <div className='maintenance-calendar-event-header'>
          <span className='maintenance-calendar-event-number'>#{record.number ?? '-'}</span>
          <Tag color={statusDisplay.color} className='maintenance-calendar-event-status'>
            {statusDisplay.label}
          </Tag>
        </div>
        <div className='maintenance-calendar-event-text maintenance-calendar-event-client'>{getClientName(record)}</div>
        <div className='maintenance-calendar-event-text'>{getWorkPlaceName(record)}</div>
      </div>
    );
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const handleViewRecord = (record: any, actionType: number, reschedulingReason?: any) => {
    if (actionType == 4) {
      const url = `/service-maintenance/interventionFile/new?mentenancePlanningId=${selectedRecord?.id}&companyId=${
        selectedRecord?.company?.id
      }&workPlaceId=${selectedRecord?.workPlace?.id}&systemTypes=${selectedRecord?.mentenancePlanningSystemTypes?.map(
        (s: any) => s?.systemType?.name,
      )}&assignedTechnicianId=${selectedRecord?.assignedTechnicianId}`;
      window.open(url, '_blank');
    } else {
      saveMentenancePlanning({
        ...record,
        statusId: actionType,
        history: [
          ...record.history,
          actionType == 5
            ? `[Cerere reprogramare] - ${dayjs().format('DD-MM-YYYY HH:mm')}] ${
                'Motivul reprogramarii: ' + !!reschedulingReason ? reschedulingReason : ''
              }`
            : actionType == 3
              ? `[A ajuns] - ${dayjs().format('DD-MM-YYYY HH:mm')}]`
              : actionType == 4
                ? `[Finalizata] - ${dayjs().format('DD-MM-YYYY HH:mm')}]`
                : '',
        ],
      });
    }

    setIsModalVisible(false);
  };

  const baseColumns: any = [
    { title: 'Numar', dataIndex: 'number', render: (number: any) => <Tag color={'blue'}>{number}</Tag> },
    {
      title: 'Beneficiar',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
    },
    {
      title: 'Sistem',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>
          {mentenancePlanningSystemTypes && mentenancePlanningSystemTypes.length > 0 && (
            <span>
              {mentenancePlanningSystemTypes.length > 0
                ? mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')
                : ''}
            </span>
          )}
        </>
      ),
    },
    {
      title: 'Data',
      key: 'maintenanceDate',
      sorter: true,
      sortOrder: sortDirection === 'asc' ? 'ascend' : 'descend',
      render: (_value: any, record: any) => <span>{formatDisplayDate(getMaintenanceDisplayDate(record))}</span>,
    },
    {
      title: 'Numar Contract',
      key: 'contractNumber',
      render: (record: any) => <>{getContractNumber(record) || '-'}</>,
    },
    { title: 'Status', dataIndex: ['status', 'name'] },
    {
      title: 'Actiuni',
      dataIndex: 'actions',
      render: (_text: any, record: any) => {
        return <Button onClick={() => handleViewInfo(record)} icon={<EditOutlined />}></Button>;
      },
      align: 'center',
    },
  ];

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxMentenancePlanning?.propsMyActiveMentenancePlannigs.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: skipValue }));
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'skip', value: 0 }));
      dispatch(updatePropsMyActiveMentenancePlannigs({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const handleTableChange: TableProps<any>['onChange'] = (_pagination, _filters, sorter, extra) => {
    if (extra.action !== 'sort') return;

    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (activeSorter?.columnKey === 'maintenanceDate' && activeSorter.order) {
      handleSortChange(activeSorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  if (isMentenancePlanningLoading || isSavingMentenancePlanning) {
    return <Spin className='spinner' size='large'></Spin>;
  }

  return (
    <div className='table-page-no-h-scroll'>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align='middle'>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Segmented
            value={viewMode}
            onChange={(v) => setViewMode((v as 'table' | 'calendar') ?? 'table')}
            options={[
              { label: 'Tabel', value: 'table', icon: <UnorderedListOutlined /> },
              { label: 'Calendar', value: 'calendar', icon: <CalendarOutlined /> },
            ]}
            style={{ width: '100%' }}
            block
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            mode='multiple'
            allowClear
            placeholder='Status'
            value={selectedStatusFilterIds}
            onChange={handleStatusFilterChange}
            options={statusFilterOptions}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={4}>
          <Segmented
            value={periodMode}
            onChange={(value) => handlePeriodModeChange((value as MaintenancePeriodMode) ?? 'month')}
            options={[
              { label: 'Saptamana', value: 'week' },
              { label: 'Luna', value: 'month' },
              { label: 'An', value: 'year' },
            ]}
            style={{ width: '100%' }}
            block
          />
        </Col>
        <Col xs={12} sm={12} md={6} lg={3}>
          <DatePicker
            picker={periodMode === 'year' ? 'year' : periodMode === 'month' ? 'month' : 'week'}
            value={getDatePickerValue(periodMode, periodFocusDate)}
            onChange={handlePeriodDateChange}
            format={getDatePickerFormat(periodMode)}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={3}>
          <Select
            value={sortDirection}
            onChange={handleSortChange}
            options={MAINTENANCE_SORT_OPTIONS}
            disabled={viewMode === 'calendar'}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align='middle'>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Select
            mode='multiple'
            allowClear
            showSearch
            maxTagCount='responsive'
            optionFilterProp='label'
            placeholder='Client'
            value={selectedCompanyIds}
            onChange={handleClientFilterChange}
            options={clientFilterOptions}
            loading={isFilterOptionsLoading}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Select
            mode='multiple'
            allowClear
            showSearch
            maxTagCount='responsive'
            optionFilterProp='label'
            placeholder='Puncte de lucru'
            value={selectedWorkPlaceIds}
            onChange={handleWorkPlaceFilterChange}
            options={workPlaceFilterOptions}
            loading={isFilterOptionsLoading}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Select
            mode='multiple'
            allowClear
            maxTagCount='responsive'
            optionFilterProp='label'
            placeholder='Perioade'
            value={selectedPeriodicityFilters}
            onChange={handlePeriodicityFilterChange}
            options={periodicityFilterOptions}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Select
            mode='multiple'
            allowClear
            showSearch
            maxTagCount='responsive'
            optionFilterProp='label'
            placeholder='Tipuri sistem'
            value={selectedSystemTypeIds}
            onChange={handleSystemTypeFilterChange}
            options={systemTypeFilterOptions}
            loading={isFilterOptionsLoading}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>

      {viewMode === 'table' && (
        <div className='table-scroll-wrapper'>
          <Table
            columns={baseColumns}
            dataSource={mentenancePlannings}
            pagination={paginationConfig}
            rowKey='number'
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
          />
        </div>
      )}

      {viewMode === 'calendar' && (
        <div className='maintenance-calendar-wrapper' style={{ minHeight: 360 }}>
          <FullCalendar
            key={`${periodMode}-${periodFocusDate.format('YYYY-MM-DD')}`}
            plugins={[dayGridPlugin, multiMonthPlugin]}
            initialView={VIEW_TO_FULLCALENDAR[periodMode]}
            initialDate={periodFocusDate.format('YYYY-MM-DD')}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: '' }}
            locale='ro'
            firstDay={1}
            dayMaxEventRows={periodMode === 'year' ? 1 : 2}
            events={calendarEvents}
            eventClick={handleCalendarEventClick}
            eventContent={renderEventContent}
            datesSet={handleDatesSet}
            height='auto'
          />
        </div>
      )}

      <MentenancePlanningModal
        isModalVisible={isModalVisible}
        handleModalClose={handleModalClose}
        selectedRecord={selectedRecord}
        handleViewRecord={handleViewRecord}
        haveActions={true}
      />
    </div>
  );
};

export default MyActiveMentenancePlanning;
